import { useHref, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { dao } from '@platform/dao';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useCreateRecordMutation, useDeleteRecordMutation, useMeQuery } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { rightPanelChanged } from '@platform/state/slices/uiSlice';
import { saveFile } from '../../list/saveFile';
import { isFieldChange } from '../draftChanges';
import { recordCode } from '../header/recordCode';
import { duplicateValues } from './duplicateValues';
import type { IRecordActions } from './IRecordActions';
import type { IRecordActionsInput } from './IRecordActionsInput';
import { recordJson } from './recordJson';
import { useCopyFeedback } from './useCopyFeedback';

/**
 * Дії панелі запису (прототип v4): «Зберегти · N», «Скасувати» (з «Скасувати ваші зміни?», якщо є зміни), «Видалити» з підтвердженням
 * назвою запису, «Призначити мені» (у чернетку під час редагування, інакше одразу, якщо користувач ще не виконавець), «Дублювати» (копія відкривається), «Копіювати
 * посилання» (✓ на 1,6 с), друк, експорт у JSON, «Запитати ШІ» (відкриває панель асистента з запитом про запис — подія po:ai-ask).
 */
export function useRecordActions({ ws, module, rt, record, editor, confirm, release }: IRecordActionsInput): IRecordActions {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: me } = useMeQuery();
  const [remove] = useDeleteRecordMutation();
  const [create] = useCreateRecordMutation();
  const link = useCopyFeedback();
  const href = useHref(routes.record(module.module.key, rt.key, record.id));
  const singular = strings.entity(rt.key, 'singular', rt.singular ?? rt.name);
  const moduleName = strings.moduleName(module.module.name);
  const discard = async () => !editor.dirty || confirm({
    title: t('recordPage.confirmDiscardTitle'), text: t('recordPage.confirmDiscard', { count: editor.dirtyKeys.length }),
    confirmLabel: t('recordPage.discard'), cancelLabel: t('recordPage.keepEditing'), danger: true,
  });
  return {
    save: () => void editor.save(),
    cancel: () => void discard().then((ok) => ok && editor.cancelEdit()),
    remove: () => void confirm({
      title: t('recordPage.confirmDeleteTitle'), confirmLabel: t('recordPage.delete'), danger: true,
      text: t('recordPage.confirmDelete', { type: singular.toLocaleLowerCase(), code: recordCode(record.display, record.code), title: record.title }),
    }).then(async (ok) => {
      if (!ok) return;
      await remove({ ws, rt: rt.key, id: record.id }).unwrap().then(() => { release(); navigate(routes.list(module.module.key, rt.key)); }).catch(() => undefined);
    }),
    // Поза правкою — одразу, якщо це зміна: уже виконавцю нічого не надсилається (прототип setVal; вибір себе — не повторне призначення).
    assignMe: () => { if (!me) return; if (editor.editing) editor.setField('assignee', me.id); else if (isFieldChange(record, 'assignee', me.id)) void editor.patchNow({ assignee: me.id }); },
    duplicate: () => void create({ ws, rt: rt.key, body: { values: duplicateValues(rt, record, t('recordPage.copySuffix')), tags: record.tags } }).unwrap()
      .then((copy) => navigate(routes.record(module.module.key, rt.key, copy.id))).catch(() => undefined),
    copyLink: () => link.copy(`${window.location.origin}${href}`),
    linkCopied: link.copied,
    print: () => window.print(),
    exportJson: () => void Promise.all([dao.collaboration.links(record.id).catch(() => []), dao.collaboration.history(record.id).catch(() => [])])
      .then(([links, history]) => saveFile(new Blob([recordJson(record, moduleName, links, history)], { type: 'application/json' }), `${record.display}.json`)),
    askAi: (prompt) => {
      dispatch(rightPanelChanged('ai'));
      const text = prompt ?? t('recordPage.askAiPrompt', { code: recordCode(record.display, record.code), title: record.title });
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('po:ai-ask', { detail: { text } })), 60);
    },
  };
}
